// components/CRM/modales/SeguimientoModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel,
  Select, MenuItem, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Typography, Box, Divider,
  IconButton, Chip
} from '@mui/material';
import {
  Close as CloseIcon, Send as SendIcon, History as HistoryIcon,
  Phone as PhoneIcon, Email as EmailIcon, Chat as ChatIcon,
  Business as BusinessIcon, Note as NoteIcon
} from '@mui/icons-material';
import { getSeguimientos, createSeguimiento } from '../../../services/crmService';

const TIPOS_SEGUIMIENTO = [
  { value: 'llamada', label: '📞 Llamada', icon: <PhoneIcon /> },
  { value: 'email', label: '📧 Email', icon: <EmailIcon /> },
  { value: 'whatsapp', label: '💬 WhatsApp', icon: <ChatIcon /> },
  { value: 'reunion', label: '🤝 Reunión', icon: <BusinessIcon /> },
  { value: 'visita_obra', label: '🏗️ Visita a Obra', icon: <BusinessIcon /> },
  { value: 'nota', label: '📝 Nota', icon: <NoteIcon /> }
];

const SeguimientoModal = ({ 
  open, 
  onClose, 
  oportunidadId, 
  oportunidadNombre,
  oportunidadEmpresa 
}) => {
  const [seguimientos, setSeguimientos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nuevoSeguimiento, setNuevoSeguimiento] = useState({
    tipo: 'nota',
    descripcion: '',
    resultado: '',
    proximo_contacto: ''
  });

  useEffect(() => {
    if (open && oportunidadId) {
      cargarSeguimientos();
    }
  }, [open, oportunidadId]);

  const cargarSeguimientos = async () => {
    setLoading(true);
    try {
      const response = await getSeguimientos(oportunidadId);
      setSeguimientos(response.data?.data || []);
    } catch (error) {
      console.error('Error cargando seguimientos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGuardar = async () => {
    if (!nuevoSeguimiento.descripcion.trim()) return;
    
    try {
      await createSeguimiento({
        oportunidad_id: oportunidadId,
        ...nuevoSeguimiento
      });
      
      setNuevoSeguimiento({
        tipo: 'nota',
        descripcion: '',
        resultado: '',
        proximo_contacto: ''
      });
      
      await cargarSeguimientos();
    } catch (error) {
      console.error('Error guardando:', error);
    }
  };

  const getTipoInfo = (tipo) => TIPOS_SEGUIMIENTO.find(t => t.value === tipo) || TIPOS_SEGUIMIENTO[5];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#3b82f6', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Seguimientos
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {oportunidadNombre} {oportunidadEmpresa && `- ${oportunidadEmpresa}`}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {/* Formulario nuevo seguimiento */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Nuevo Seguimiento
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={nuevoSeguimiento.tipo}
                  onChange={(e) => setNuevoSeguimiento({...nuevoSeguimiento, tipo: e.target.value})}
                >
                  {TIPOS_SEGUIMIENTO.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                size="small"
                label="Descripción"
                value={nuevoSeguimiento.descripcion}
                onChange={(e) => setNuevoSeguimiento({...nuevoSeguimiento, descripcion: e.target.value})}
                placeholder="¿Qué conversaste con el cliente?"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Resultado / Próximo paso"
                value={nuevoSeguimiento.resultado}
                onChange={(e) => setNuevoSeguimiento({...nuevoSeguimiento, resultado: e.target.value})}
                placeholder="¿Qué acordaron? ¿Qué sigue?"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Próximo contacto"
                value={nuevoSeguimiento.proximo_contacto}
                onChange={(e) => setNuevoSeguimiento({...nuevoSeguimiento, proximo_contacto: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleGuardar}
                disabled={!nuevoSeguimiento.descripcion.trim()}
                sx={{ height: '100%' }}
              >
                Guardar
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Lista de seguimientos */}
        <Typography variant="subtitle2" gutterBottom>
          Historial ({seguimientos.length})
        </Typography>
        
        {seguimientos.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography>No hay seguimientos registrados</Typography>
          </Box>
        ) : (
          <List>
            {seguimientos.map((seg, index) => {
              const tipoInfo = getTipoInfo(seg.tipo);
              return (
                <ListItem
                  key={seg.id}
                  sx={{
                    mb: 1,
                    bgcolor: 'white',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: '#3b82f6' }}>
                      {tipoInfo.icon}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={tipoInfo.label} 
                          size="small" 
                          sx={{ fontSize: '0.75rem' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(seg.creado_en).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {seg.descripcion}
                        </Typography>
                        {seg.resultado && (
                          <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
                            → {seg.resultado}
                          </Typography>
                        )}
                        {seg.proximo_contacto && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Próximo contacto: {new Date(seg.proximo_contacto).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeguimientoModal;